import { chunk } from 'lodash';
import fetchWithOptions from '../../../shared/utils/fetch';
import { Ecg } from '../../ecg/types/ecg.type';
import {
  Cluster,
  ClusterBeat,
  ClusterOnsetsOffsets,
  ServerChildren,
  ServerCluster,
  ServerReclassification,
} from '../clustering.types';
import {
  beatsToServerOnsetsOffsets,
  CLUSTER_CODES,
  filterCategories,
  getDurationBatches,
  getNeighborRRs,
  handleEdgeBeats,
  jsonToBeats,
  shiftToCenter,
  SIDE_POINTS,
} from './helpers';

export const fetchClusters = async (
  ecgId: string,
): Promise<ServerCluster[] | null> => {
  try {
    return await fetchWithOptions<ServerCluster[]>(
      `/api/ecg/${ecgId}/wave_clusters?show_raw_format=1`,
    );
  } catch (e) {
    return null;
  }
};

const ONSET_OFFSET_CHUNK = 200;

interface ClustersWithBatches extends ServerCluster {
  chunks: ServerCluster['onsets_offsets'][];
}

export const getClusters = async (ecg: Ecg) => {
  try {
    return fetchClusters(ecg.id).then(async (serverClusters) => {
      if (serverClusters == null) {
        return null;
      }
      const filteredClusters = serverClusters.filter(filterCategories);
      const filteredBaseClusters: Record<number, Cluster> = filteredClusters
        .filter((serverCluster) => serverCluster.has_children === false)
        .reduce(
          (acc, serverCluster) => ({
            ...acc,
            [serverCluster.id]: serverCluster,
          }),
          {},
        );
      const filteredMorphologies: Record<number, Cluster> = filteredClusters
        .filter((serverCluster) => serverCluster.has_children === true)
        .reduce(
          (acc, serverCluster) => ({
            ...acc,
            [serverCluster.id]: serverCluster,
          }),
          {},
        );
      const allClusters = await constructClusters(ecg, filteredClusters);
      const morphologies = allClusters
        .filter((morphology) => !!filteredMorphologies[morphology.id])
        .map((morphology, index) => ({ ...morphology, priority: index }));
      const baseClusters = allClusters
        .filter((baseCluster) => !!filteredBaseClusters[baseCluster.id])
        .map((baseCluster, index) => ({ ...baseCluster, priority: index }));

      return {
        baseClusters,
        morphologies,
      };
    });
  } catch (e) {
    return null;
  }
};

export const constructClusters = async (
  ecg: Ecg,
  serverClusters: ServerCluster[],
) => {
  const clustersWithBatches: ClustersWithBatches[] = serverClusters.map(
    (serverCluster) => {
      const beatBatches = chunk(
        serverCluster.onsets_offsets,
        ONSET_OFFSET_CHUNK,
      );

      return {
        ...serverCluster,
        chunks: beatBatches,
      };
    },
  );

  const clusters: Promise<Cluster>[] = await clustersWithBatches.map(
    async (clusterBatch, i) => {
      const clusterBeatsPromises = clusterBatch.chunks.map(
        async (chunk) => await getClusterBeats(ecg, jsonToBeats(chunk)),
      );
      const clusterBeats = await Promise.all(clusterBeatsPromises);

      return {
        id: clusterBatch.id,
        beats: clusterBeats.flatMap((a) => a),
        classification: CLUSTER_CODES[clusterBatch.category_code],
        priority: i,
      };
    },
  );

  return await Promise.all(clusters);
};

export const getClusterBeats = async (
  ecg: Ecg,
  clusterBeats: ClusterBeat[],
): Promise<ClusterBeat[]> => {
  try {
    // ecg.duration + 1 is the limit from the backend
    const batches = getDurationBatches(clusterBeats, ecg.duration + 1);
    const serverClusterData = await fetchWithOptions<ClusterOnsetsOffsets[]>(
      `/api/ecg/${ecg.id}/slice/batch/?show_delineation=1${batches}`,
    );
    return serverClusterData.map((serverClusterFile, i) => {
      const leads = Object.keys(serverClusterFile.data);

      const shiftedData = shiftToCenter(
        serverClusterFile.data[leads[0]],
        serverClusterFile.mV_per_unit,
        SIDE_POINTS,
      );

      const beat = clusterBeats[i];
      const data = handleEdgeBeats(
        shiftedData,
        beat,
        SIDE_POINTS,
        ecg.duration + 1,
      );

      const { nextRR, previousRR, currentRR } = getNeighborRRs(
        clusterBeats[i],
        serverClusterFile.meta.delineation,
      );

      return {
        ...clusterBeats[i],
        data,
        currentRR,
        previousRR,
        nextRR,
      };
    });
  } catch (e) {
    return [];
  }
};

export const createChildren = async (
  ecgId: string,
  cluster: Cluster,
  selectedBeats: ClusterBeat[],
  unselectedBeats: ClusterBeat[],
): Promise<{
  cluster1: Cluster;
  cluster2: Cluster;
} | null> => {
  try {
    const unselectedToServerData = beatsToServerOnsetsOffsets(unselectedBeats);
    const selectedToServerData = beatsToServerOnsetsOffsets(selectedBeats);

    const serverClusterChildren = await fetchWithOptions<ServerChildren>(
      `/api/ecg/${ecgId}/wave_clusters/${cluster.id}/create_children`,
      {
        method: 'POST',
        body: JSON.stringify({
          wave_sublist_list: [unselectedToServerData, selectedToServerData],
        }),
      },
    );
    const parentId = serverClusterChildren.parent_cluster.id;
    await splitParent(ecgId, parentId);

    const entries = Object.entries(
      serverClusterChildren.child_cluster_id_to_wave_list,
    );
    // first cluster id of child_cluster_id_to_wave_list
    let unselectedClusterId = parseInt(entries[0][0]);
    // second cluster id of child_cluster_id_to_wave_list
    let selectedClusterId = parseInt(entries[1][0]);

    for (const [key, value] of entries) {
      // onsetsOffsets --> [onset, offset, id, value for the beat to be ignored]
      // You can see the type of onsetsOffsets clustering.types.ts
      const isUnselected = value.find(
        (onsetsOffsets) => onsetsOffsets[2] === unselectedBeats[0].id,
      );
      const isSelected = value.find(
        (onsetsOffsets) => onsetsOffsets[2] === selectedBeats[0].id,
      );
      if (isUnselected) {
        unselectedClusterId = parseInt(key);
      } else if (isSelected) {
        selectedClusterId = parseInt(key);
      }
    }

    const cluster1: Cluster = {
      id: unselectedClusterId,
      classification: cluster.classification,
      beats: unselectedBeats,
      priority: cluster.priority,
    };

    const cluster2: Cluster = {
      id: selectedClusterId,
      classification: cluster.classification,
      beats: selectedBeats,
      priority: cluster.priority + 1,
    };

    return { cluster1, cluster2 };
  } catch (e) {
    return null;
  }
};

export const splitParent = async (
  ecgId: string,
  parentClusterId: number,
): Promise<Omit<ServerCluster, 'onsets_offsets'>[] | null> => {
  try {
    const response = await fetchWithOptions<
      Omit<ServerCluster, 'onsets_offsets'>[]
    >(`/api/ecg/${ecgId}/wave_clusters/${parentClusterId}/split`, {
      method: 'POST',
    });
    return response;
  } catch (e) {
    return null;
  }
};

/**
 * Reclassifying creates a new cluster with
 * the new category code and the same onsets and offsets
 * @param ecgId
 * @param clusterIds
 * @param categoryCode
 * @returns the id of the new cluster
 */
export const reclassify = async (
  ecgId: string,
  clusterIds: number[],
  categoryCode: string,
): Promise<number[] | null> => {
  try {
    const response = await fetchWithOptions<ServerReclassification>(
      `/api/ecg/${ecgId}/wave_clusters/reclassify`,
      {
        method: 'POST',
        body: JSON.stringify({
          wave_cluster_ids: clusterIds,
          category_code: categoryCode,
        }),
      },
    );
    return response.new_wave_clusters.map(
      (newWaveCluster) => newWaveCluster.id,
    );
  } catch (e) {
    return null;
  }
};

/**
 * mer creates a new cluster with
 * the new category code and the same onsets and offsets
 * @param ecgId
 * @param clusterIds
 * @param categoryCode
 * @returns the id of the new cluster
 */
export const merge = async (
  ecgId: string,
  clusterIds: number[],
): Promise<number | null> => {
  try {
    const response = await fetchWithOptions<
      Omit<ServerCluster, 'onsets_offsets'>
    >(`/api/ecg/${ecgId}/wave_clusters/merge`, {
      method: 'POST',
      body: JSON.stringify({
        wave_cluster_ids: clusterIds,
      }),
    });
    return response.id;
  } catch (e) {
    return null;
  }
};
